import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  Grid,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Location } from '../../../../core/interfaces/location.interface';

interface LocationInfoProps {
  location: Location;
  onClear: () => void;
}

export const LocationInfo: React.FC<LocationInfoProps> = ({ location, onClear }) => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6" color="primary">
              Ubicación Seleccionada
            </Typography>
          </Box>
          <IconButton 
            onClick={onClear} 
            size="small"
            color="error"
            title="Limpiar selección"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Alerta de copia exitosa */}
        {copySuccess && (
          <Alert severity="success" onClose={() => setCopySuccess(null)} sx={{ mb: 2 }}>
            {copySuccess}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Código de ubicación */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Código:
              </Typography>
              <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                {location.code}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyToClipboard(location.code || '')}
                sx={{ 
                  color: 'text.secondary', 
                  '&:hover': { 
                    color: 'primary.main', 
                    backgroundColor: 'action.hover' 
                  } 
                }}
                title="Copiar código al portapapeles"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Descripción */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Descripción:
              </Typography>
              <Typography variant="body1">
                {location.description || 'Sin descripción'}
              </Typography>
            </Box>
          </Grid>

          {/* Nivel */}
          {location.level && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Nivel:
                </Typography>
                <Typography variant="body1">
                  {location.level}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Pasillo */}
          {location.aisle && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Pasillo:
                </Typography>
                <Typography variant="body1">
                  {location.aisle}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Columna */}
          {location.column && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Columna:
                </Typography>
                <Typography variant="body1">
                  {location.column}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LocationInfo;
